import styled from '@emotion/styled';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { QcsTypography } from '../../common/basic/QcsTypography';
import { IconButton, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

interface Props {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  disabled?: boolean;
}

const TitleWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  minWidth: 0,
});

const ReadOnlyTitle = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  cursor: 'pointer',
  borderRadius: '4px',
  padding: '2px 6px',
  margin: '-2px -6px',
  transition: 'background-color 0.15s ease',
  minWidth: 0,
  '& .edit-icon': {
    opacity: 0.4,
    fontSize: '1rem',
    transition: 'opacity 0.15s ease',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    '& .edit-icon': {
      opacity: 0.8,
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .edit-icon': {
      opacity: 0.5,
    },
  },
}));

const EditContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  minWidth: 0,
  width: '100%',
});

const TitleInput = styled.input(({ theme }) => ({
  font: 'inherit',
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.h4.fontWeight,
  lineHeight: theme.typography.h4.lineHeight,
  letterSpacing: (theme.typography.h4 as any).letterSpacing ?? 'inherit',
  color: theme.palette.text.primary,
  background: 'transparent',
  border: 'none',
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 0,
  outline: 'none',
  padding: '2px 2px 4px',
  boxSizing: 'border-box',
  maxWidth: '100%',
  minWidth: 0,
  '&:focus': {
    borderBottomColor: theme.palette.primary.dark,
  },
}));

// Hidden mirror of the input text used to size the input to its content.
const TitleSizer = styled.span(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  whiteSpace: 'pre',
  font: 'inherit',
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.h4.fontWeight,
  lineHeight: theme.typography.h4.lineHeight,
  letterSpacing: theme.typography.h4.letterSpacing ?? 'inherit',
  padding: '2px 2px 4px',
}));

const ActionButtons = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.125rem',
  flexShrink: 0,
});

export const InlineEditableTitle: FC<Props> = ({ value, onSave, disabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [inputWidth, setInputWidth] = useState<number>();
  const inputRef = useRef<HTMLInputElement>(null);
  const sizerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Size the input to its text content (capped to the available width via maxWidth).
  useLayoutEffect(() => {
    if (isEditing && sizerRef.current) {
      setInputWidth(sizerRef.current.offsetWidth + 2);
    }
  }, [isEditing, editValue]);

  const startEditing = () => {
    if (disabled) return;
    setEditValue(value);
    setIsEditing(true);
  };

  useEffect(() => {
    if (!isEditing || isSaving) return;
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        discard();
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, isSaving, value]);

  const discard = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const save = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === value) {
      discard();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      discard();
    }
  };

  if (isEditing) {
    return (
      <TitleWrapper>
        <EditContainer>
          <TitleSizer ref={sizerRef} aria-hidden>
            {editValue || ' '}
          </TitleSizer>
          <TitleInput
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            style={{ width: inputWidth }}
          />
          <ActionButtons>
            {isSaving ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <IconButton
                  size="small"
                  sx={{ color: 'success.main' }}
                  onClick={save}
                  disabled={!editValue.trim() || editValue.trim() === value}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: 'error.main' }} onClick={discard}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </ActionButtons>
        </EditContainer>
      </TitleWrapper>
    );
  }

  if (disabled) {
    return (
      <TitleWrapper>
        <QcsTypography
          variant="h4"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {value}
        </QcsTypography>
      </TitleWrapper>
    );
  }

  return (
    <TitleWrapper>
      <ReadOnlyTitle onClick={startEditing}>
        <QcsTypography
          variant="h4"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {value}
        </QcsTypography>
        <EditIcon className="edit-icon" />
      </ReadOnlyTitle>
    </TitleWrapper>
  );
};
